/**
 * shared/components/AdminShortlistFlow.jsx
 * -----------------------------------------
 * Modern full-screen shortlist drawer for ADMIN.
 *
 * Replaces the legacy `<ShortlistDialog>` (tiny max-w-2xl modal with text-only
 * checkbox property list) with the same horizontal-card multiselect browsing
 * experience the team caller workflow uses.
 *
 *   <AdminShortlistFlow
 *     open
 *     onOpenChange
 *     mode="create" | "edit" | "bulk"
 *     editingId={sid}                  // mode="edit"
 *     initialPropertyIds={[id, id]}    // mode="bulk" (pre-selected from listings)
 *     allProperties={[...]}
 *     onSaved={(bundle)=>}
 *   />
 *
 * Modes:
 *  • create — blank state; user enters name + multi-selects → POST /admin/shortlists
 *  • edit   — loads /admin/shortlists/{sid}; user edits items + meta → PUT
 *  • bulk   — pre-selected ids from admin listings selection; user names + saves → POST
 *
 * Layout:
 *   Header (close · "New shortlist" / bundle name · Save)
 *   Inline meta strip (name · client_name · notes) — collapsible
 *   In-shortlist horizontal rail (current items)
 *   Sticky search
 *   PropertyCard list (vertical stack) with selectionMode=true
 *   Sticky action bar: "Add N to shortlist"
 *
 * v2.71.55 — Initial.
 */

import React, { useEffect, useMemo, useState } from "react";
import api, { apiErrorMsg } from "../../lib/api";
import { toast } from "sonner";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "../../components/ui/sheet";

import {
  X, Search, Building2, Plus, ClipboardCopy, ExternalLink, MessageCircle, Trash2,
} from "lucide-react";

import PropertyCard from "./PropertyCard";
import { useSelection } from "./SelectionToolbar";
import { getPropertyThumbnail } from "../utils/property";

const fmtINR = (n) => {
  const num = Number(n) || 0;
  if (num >= 10000000) return `₹${(num / 10000000).toFixed(2)} Cr`;
  if (num >= 100000)   return `₹${(num / 100000).toFixed(2)} L`;
  if (num >= 1000)     return `₹${(num / 1000).toFixed(1)}K`;
  return `₹${num}`;
};

export default function AdminShortlistFlow({
  open,
  onOpenChange,
  mode = "create",
  editingId = null,
  initialPropertyIds = [],
  allProperties = [],
  onSaved,
  testidPrefix = "asf",
}) {
  // ── bundle-side state
  const [name, setName] = useState("");
  const [clientName, setClientName] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [bundle, setBundle] = useState(null);     // edit mode only
  const [pids, setPids] = useState([]);           // current property_ids in this bundle
  const [busy, setBusy] = useState(false);

  // ── browse-side state
  const [q, setQ] = useState("");
  const sel = useSelection();

  // ── reset on open
  useEffect(() => {
    if (!open) return;
    sel.clear();
    setQ("");
    if (mode === "edit" && editingId) {
      setBusy(true);
      api.get(`/admin/shortlists/${editingId}`)
        .then((r) => {
          const b = r.data || {};
          setBundle(b);
          setName(b.name || "");
          setClientName(b.client_name || "");
          setClientPhone(b.client_phone || "");
          setNotes(b.notes || "");
          setPids((b.items || []).map((i) => i.property_id));
        })
        .catch((e) => toast.error(apiErrorMsg(e, "Failed to load shortlist")))
        .finally(() => setBusy(false));
    } else if (mode === "bulk") {
      setBundle(null);
      setName("");
      setClientName("");
      setClientPhone("");
      setNotes("");
      setPids(initialPropertyIds || []);
    } else {
      setBundle(null);
      setName("");
      setClientName("");
      setClientPhone("");
      setNotes("");
      setPids([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, editingId]);

  const inBundle = useMemo(() => new Set(pids), [pids]);

  const filtered = useMemo(() => {
    const list = Array.isArray(allProperties) ? allProperties : [];
    if (!q.trim()) return list.slice(0, 100);
    const t = q.toLowerCase();
    return list.filter((p) =>
      [p.title, p.area, p.main_area, p.sub_area, p.bhk, p.property_type]
        .filter(Boolean).join(" ").toLowerCase().includes(t)
    ).slice(0, 100);
  }, [allProperties, q]);

  // Lookups for the in-shortlist horizontal rail
  const propById = useMemo(() => {
    const m = {};
    (allProperties || []).forEach((p) => { m[p.id] = p; });
    if (bundle?.items) bundle.items.forEach((it) => { m[it.property_id] = m[it.property_id] || it; });
    return m;
  }, [allProperties, bundle]);

  const addSelected = () => {
    if (sel.count === 0) return;
    const next = Array.from(new Set([...pids, ...sel.idList]));
    setPids(next);
    toast.success(`Added ${sel.count} ${sel.count === 1 ? "property" : "properties"}`);
    sel.clear();
  };

  const removePid = (pid) => {
    setPids((prev) => prev.filter((x) => x !== pid));
  };

  const save = async () => {
    if (!name.trim()) {
      toast.error("Shortlist name required");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: name.trim(),
        client_name: clientName.trim(),
        client_phone: clientPhone.trim(),
        notes: notes.trim(),
        property_ids: pids,
        assigned_to: bundle?.assigned_to || [],
      };
      const r = bundle?.id
        ? await api.put(`/admin/shortlists/${bundle.id}`, payload)
        : await api.post("/admin/shortlists", payload);
      toast.success(bundle?.id ? "Updated" : "Created");
      onSaved?.(r.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(apiErrorMsg(e, "Save failed"));
    } finally { setBusy(false); }
  };

  const copyLink = async () => {
    if (!bundle?.slug) return;
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/shortlist/${bundle.slug}`);
      toast.success("Link copied");
    } catch { toast.error("Copy failed"); }
  };

  const tid = (s) => `${testidPrefix}-${s}`;
  const isEditing = !!bundle?.id;
  const headerTitle = isEditing ? "Edit shortlist" : mode === "bulk" ? "Add to new shortlist" : "New shortlist";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="p-0 h-[100dvh] sm:h-[95vh] sm:max-w-3xl sm:mx-auto sm:rounded-t-2xl flex flex-col w-full"
        data-testid={tid("root")}
      >
        {/* Header */}
        <SheetHeader className="px-4 pt-4 pb-3 border-b bg-white">
          <div className="flex items-start justify-between gap-3">
            <button
              data-testid={tid("close")}
              onClick={() => onOpenChange(false)}
              aria-label="Close"
              className="shrink-0 -ml-1 inline-flex items-center justify-center w-9 h-9 rounded-md text-slate-600 hover:bg-slate-100"
            >
              <X size={18} />
            </button>
            <div className="flex-1 min-w-0 text-left">
              <SheetTitle className="text-[15px] font-semibold text-slate-900 truncate">
                {headerTitle}
              </SheetTitle>
              <SheetDescription className="text-[12px] text-slate-500 truncate mt-0.5">
                {isEditing ? `${pids.length} ${pids.length === 1 ? "property" : "properties"}` : "Curate a bundle to share with clients"}
              </SheetDescription>
            </div>
            <Button
              data-testid={tid("save")}
              onClick={save}
              disabled={busy || !name.trim()}
              size="sm"
              className="shrink-0 bg-indigo-600 hover:bg-indigo-700 h-9"
            >
              {busy ? "Saving…" : "Save"}
            </Button>
          </div>
        </SheetHeader>

        {/* Meta strip */}
        <div className="px-4 py-3 border-b bg-slate-50/50 grid grid-cols-2 gap-2" data-testid={tid("meta")}>
          <div className="col-span-2">
            <label className="text-[10px] font-medium text-slate-600 uppercase tracking-wide">Name *</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="h-9 mt-0.5 text-[13px] bg-white" placeholder="3BHK picks for Mr. Sharma" data-testid={tid("name")} />
          </div>
          <div>
            <label className="text-[10px] font-medium text-slate-600 uppercase tracking-wide">Client name</label>
            <Input value={clientName} onChange={(e) => setClientName(e.target.value)} className="h-9 mt-0.5 text-[13px] bg-white" data-testid={tid("client-name")} />
          </div>
          <div>
            <label className="text-[10px] font-medium text-slate-600 uppercase tracking-wide">Phone</label>
            <Input value={clientPhone} onChange={(e) => setClientPhone(e.target.value)} className="h-9 mt-0.5 text-[13px] bg-white" data-testid={tid("client-phone")} />
          </div>
          <div className="col-span-2">
            <label className="text-[10px] font-medium text-slate-600 uppercase tracking-wide">Notes</label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={1} className="mt-0.5 text-[13px] bg-white" data-testid={tid("notes")} />
          </div>
        </div>

        {/* In-shortlist rail */}
        {pids.length > 0 && (
          <div className="px-3 pt-3 border-b bg-slate-50/60" data-testid={tid("rail-card")}>
            <div className="flex items-center justify-between mb-1.5 px-1">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                In shortlist ({pids.length})
              </div>
              {bundle?.slug && (
                <button
                  data-testid={tid("copy-link")}
                  onClick={copyLink}
                  className="text-[11px] text-indigo-600 hover:text-indigo-700 inline-flex items-center gap-1"
                >
                  <ClipboardCopy size={11} /> Copy link
                </button>
              )}
            </div>
            <div
              data-testid={tid("rail")}
              className="flex gap-2 overflow-x-auto snap-x snap-mandatory pb-3 -mx-3 px-3"
              style={{ scrollbarWidth: "thin" }}
            >
              {pids.map((pid) => {
                const p = propById[pid] || { id: pid, title: "Property", price: 0 };
                const cover = getPropertyThumbnail(p);
                return (
                  <div
                    key={pid}
                    data-testid={tid(`rail-${pid}`)}
                    className="snap-start shrink-0 w-32 bg-white border border-slate-200 rounded-lg overflow-hidden"
                  >
                    <div className="aspect-[4/3] bg-slate-100 relative">
                      <div className="absolute inset-0 flex items-center justify-center text-slate-300"><Building2 size={18} /></div>
                      {cover && (
                        <img
                          src={cover}
                          alt={p.title || "Property"}
                          loading="lazy"
                          onError={(e) => { e.currentTarget.style.display = "none"; }}
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                      )}
                      <button
                        data-testid={tid(`rail-remove-${pid}`)}
                        onClick={() => removePid(pid)}
                        className="absolute top-1 right-1 w-5 h-5 rounded-full bg-slate-900/70 hover:bg-rose-600 text-white inline-flex items-center justify-center"
                        aria-label="Remove from shortlist"
                      >
                        <X size={11} />
                      </button>
                    </div>
                    <div className="px-2 py-1.5">
                      <div className="text-[11.5px] font-medium text-slate-900 line-clamp-1">{p.title || p.title_snapshot}</div>
                      <div className="text-[10.5px] text-slate-500 mt-0.5">{fmtINR(p.price ?? p.price_snapshot)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Sticky search */}
        <div className="px-3 py-2 border-b bg-white sticky top-0 z-10">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid={tid("search")}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search properties to add…"
              className="pl-9 h-10 text-sm"
            />
            {q && (
              <button
                onClick={() => setQ("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
              >
                <X size={13} />
              </button>
            )}
          </div>
        </div>

        {/* Browse list */}
        <div className="flex-1 overflow-y-auto px-3 py-2 pb-32 bg-slate-50" data-testid={tid("browse-list")}>
          {filtered.length === 0 && (
            <div className="text-center text-[13px] text-slate-500 py-12">
              {q ? "No properties match your search" : "No properties available"}
            </div>
          )}
          <div className="space-y-2">
            {filtered.map((p) => {
              const inList = inBundle.has(p.id);
              return (
                <PropertyCard
                  key={p.id}
                  property={p}
                  variant="team"
                  selected={sel.has(p.id) || inList}
                  selectionMode={true}
                  onToggleSelect={(id) => {
                    if (inList) { toast.info("Already in this shortlist"); return; }
                    sel.toggle(id);
                  }}
                  testidPrefix={`${testidPrefix}-prop`}
                />
              );
            })}
          </div>
        </div>

        {/* Sticky action bar */}
        <div
          className="border-t bg-white px-3 py-2.5 shrink-0"
          style={{ paddingBottom: "max(env(safe-area-inset-bottom), 10px)" }}
          data-testid={tid("action-bar")}
        >
          {sel.count > 0 ? (
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={sel.clear} size="sm" className="shrink-0" data-testid={tid("clear")}>
                Clear
              </Button>
              <Button
                data-testid={tid("add-selected")}
                onClick={addSelected}
                className="flex-1 bg-indigo-600 hover:bg-indigo-700"
              >
                <Plus size={14} className="mr-1.5" /> Add {sel.count} to shortlist
              </Button>
            </div>
          ) : (
            <div className="text-[12.5px] text-center text-slate-500 py-1">
              {pids.length > 0
                ? `${pids.length} ${pids.length === 1 ? "property" : "properties"} in this shortlist — tap Save when done`
                : "Tap properties above to select"}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
